/**
 * Timezone-safe date formatting utilities.
 *
 * Problem: The DB stores dates as ISO strings with UTC midnight
 * (e.g., '1977-11-21T00:00:00.000Z'). When JavaScript parses this
 * via `new Date()`, it interprets it in UTC. Then `toLocaleDateString()`
 * renders in the browser's local timezone — shifting the date back
 * by one day for anyone west of UTC.
 *
 * Solution: Parse the date portion only and construct in local timezone,
 * preventing any UTC offset from shifting the calendar day.
 *
 * @module lib/dateUtils
 */

/**
 * Parse a date string into a local Date object without UTC timezone shift.
 * Handles ISO strings ('2026-03-30T00:00:00.000Z'), plain dates ('2026-03-30'),
 * and slash-separated formats ('03/30/2026').
 */
export function parseLocalDate(dateStr: string): Date {
  // Strip time portion if present — we only care about the calendar date
  const datePart = dateStr.split('T')[0];

  // Handle both YYYY-MM-DD and MM/DD/YYYY
  if (datePart.includes('-')) {
    const [year, month, day] = datePart.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  if (datePart.includes('/')) {
    const [month, day, year] = datePart.split('/').map(Number);
    return new Date(year, month - 1, day);
  }

  // Fallback: noon local time to avoid day-shift even in edge cases
  return new Date(dateStr + 'T12:00:00');
}

/**
 * Format a date-of-birth string for display, timezone-safe.
 *
 * @example
 * formatDOB('1977-11-21T00:00:00.000Z')
 * // => "Nov 21, 1977" (regardless of browser timezone)
 *
 * formatDOB('1977-11-21T00:00:00.000Z', { month: '2-digit', day: '2-digit', year: 'numeric' })
 * // => "11/21/1977"
 */
export function formatDOB(
  dob: string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!dob) return 'N/A';

  const date = parseLocalDate(dob);
  if (isNaN(date.getTime())) return 'Invalid date';

  return date.toLocaleDateString('en-US', options ?? {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Calculate age from a date-of-birth string, timezone-safe.
 *
 * @example
 * calculateAge('1977-11-21T00:00:00.000Z') // => 48
 */
export function calculateAge(dob: string | null | undefined): number | null {
  if (!dob) return null;

  const birthDate = parseLocalDate(dob);
  if (isNaN(birthDate.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
}

/**
 * Format any date string for display, timezone-safe.
 * Use for non-DOB dates that are stored as date-only (no time component matters).
 *
 * For dates where time matters (appointments, timestamps), use standard
 * Date formatting with explicit timezone handling instead.
 */
export function formatDateSafe(
  dateStr: string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!dateStr) return 'N/A';

  const date = parseLocalDate(dateStr);
  if (isNaN(date.getTime())) return 'Invalid date';

  return date.toLocaleDateString('en-US', options ?? {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
